/**
 * OptionsTab — full options chain with Greeks.
 *
 * Consumes /api/options/{symbol} which returns a flat OptionContract[]
 * (both calls and puts across multiple expiries). We slice client-side
 * by selected expiry, compute spot + DTE + PCR + ATM IV + skew, and
 * render calls + puts side by side with Δ Γ Θ columns.
 */
import { useState, useMemo } from 'react';
import { LazyPlot as Plot } from '@/components/LazyPlot';
import { useStore, readThemeColors } from '@/store';
import { useQuery } from '@tanstack/react-query';
import { api, isSynthetic } from '@/lib/api';
import { exposureProfile, maxPain, ivSkew, skewNumber, termStructure, type OptionContract as OC } from '@/lib/optionsAnalytics';
import type { Quote } from '@/types';
import { ORG, GRN, RED, MUT, FONT } from '@/theme';

interface OptionContract {
  symbol:       string;
  expiry:       string;
  strike:       number;
  type:         'call' | 'put';
  bid:          number;
  ask:          number;
  last:         number;
  iv:           number;
  ivRank:       number;
  delta:        number;
  gamma:        number;
  theta:        number;
  vega:         number;
  rho:          number;
  openInterest: number;
  volume:       number;
}

export function OptionsTab() {
  const focus = useStore(s => s.focusSymbol);
  const [expiryIdx, setExpiryIdx] = useState(0);

  const { data: chain, isLoading } = useQuery<OptionContract[]>({
    queryKey: ['options', focus],
    queryFn:  () => api(`/api/options/${encodeURIComponent(focus)}`),
    staleTime: 60_000,
  });

  const { data: quote } = useQuery<Quote>({
    queryKey: ['wl-quote', focus],
    queryFn:  () => api(`/api/quote/${encodeURIComponent(focus)}`),
    refetchInterval: 30_000,
    staleTime: 30_000,
  });

  // Derived: expiries, calls/puts for selected expiry, summary stats.
  const sliced = useMemo(() => {
    if (!chain || chain.length === 0) return null;
    const expiries = Array.from(new Set(chain.map(c => c.expiry))).sort();
    const expiry = expiries[Math.min(expiryIdx, expiries.length - 1)];
    const dte = Math.max(1, Math.round((new Date(expiry).getTime() - Date.now()) / 86400_000));
    const slice = chain.filter(c => c.expiry === expiry);
    const calls = slice.filter(c => c.type === 'call').sort((a, b) => a.strike - b.strike);
    const puts  = slice.filter(c => c.type === 'put') .sort((a, b) => a.strike - b.strike);

    // SPOT MUST BE REAL OR ABSENT. This fell back to `calls[0].strike` -- and
    // calls are sorted ASCENDING, so a failed quote silently became the LOWEST
    // STRIKE IN THE CHAIN. That fake spot then drove ATM IV, skew, the GEX flip
    // strike, the exposure profile and the ITM shading, and printed as "SPOT $x"
    // with no marker. Every downstream analytic inherits it, so null it here and
    // suppress rather than fabricate.
    const spotRaw = quote?.last ?? quote?.mid ?? null;
    const spot    = Number.isFinite(spotRaw as number) && (spotRaw as number) > 0 ? (spotRaw as number) : null;
    const callOI   = calls.reduce((s, c) => s + c.openInterest, 0);
    const putOI    = puts .reduce((s, c) => s + c.openInterest, 0);
    // A put/call ratio of 0.00 reads as extreme bullishness; unknown is NaN.
    const pcr      = callOI > 0 ? putOI / callOI : NaN;

    // ATM IV: average of closest call + put.
    // Without a real spot there is no "at the money": these must stay unknown.
    const atmCall = spot !== null ? nearest(calls, spot) : null;
    const atmPut  = spot !== null ? nearest(puts,  spot) : null;
    const atmIv   = (atmCall && atmPut) ? (atmCall.iv + atmPut.iv) / 2
                  : atmCall?.iv ?? atmPut?.iv ?? NaN;

    // Skew: 25Δ put IV / ATM IV (downside) and 25Δ call IV / ATM IV (upside).
    const dn = calls.find(() => true);  // fallback if no real delta
    const otmPut  = puts .reduce((best, p) => Math.abs(p.delta + 0.25) < Math.abs((best?.delta ?? -1) + 0.25) ? p : best, puts[0]);
    const otmCall = calls.reduce((best, c) => Math.abs(c.delta - 0.25) < Math.abs((best?.delta ?? 1) - 0.25)  ? c : best, calls[0]);
    // 1 means "no skew" -- a real market observation. Unknown must not claim it.
    const skewDn = Number.isFinite(atmIv) && atmIv > 0 && otmPut  ? otmPut.iv  / atmIv : NaN;
    const skewUp = Number.isFinite(atmIv) && atmIv > 0 && otmCall ? otmCall.iv / atmIv : NaN;

    void dn;
    return { expiries, expiry, dte, spot, callOI, putOI, pcr, atmIv, skewDn, skewUp, calls, puts };
  }, [chain, expiryIdx, quote]);

  // Quant analytics for the selected expiry. Pure functions over the
  // already-loaded chain — no extra requests.
  const analytics = useMemo(() => {
    if (!sliced || !chain) return null;
    // Every analytic below is a function OF SPOT. With no real spot they would
    // all be computed against a fabricated one, so suppress the whole block
    // rather than render confident numbers derived from a failed quote.
    if (sliced.spot === null) return null;
    const spotNum = sliced.spot;
    const sliceChain = chain.filter(c => c.expiry === sliced.expiry) as unknown as OC[];
    const profile = exposureProfile(sliceChain, spotNum);
    const mp      = maxPain(sliceChain);
    const skew    = ivSkew(sliceChain, spotNum);
    const skewN   = skewNumber(sliceChain);
    const term    = termStructure(chain as unknown as OC[], spotNum);
    return { profile, maxPainStrike: mp, skew, skewN, term };
  }, [sliced, chain]);

  // Theme-aware colors for the analytics charts.
  const { gain: GAIN, loss: LOSS, accent: ACC } = readThemeColors();

  if (isLoading) return <Cent>LOADING CHAIN...</Cent>;
  if (!sliced)   return <Cent>NO OPTIONS DATA FOR {focus}</Cent>;

  return (
    <div style={{ fontFamily: FONT, color: '#e0e0e0' }}>
      {isSynthetic('/api/options/') && (
        <div style={{
          border: '1px solid ' + ORG, background: 'rgba(255,153,0,0.10)',
          padding: '5px 9px', marginBottom: 8, fontSize: 10.5, color: ORG,
        }}>
          <strong>SIMULATED DATA.</strong>{' '}
          <span style={{ color: MUT }}>
            No options feed is connected — this chain, its Greeks, IV, GEX and
            max-pain are generated from a Black-Scholes model, not the market.
          </span>
        </div>
      )}
      {/* Expiry pills */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ color: MUT, fontSize: 10, letterSpacing: 1 }}>EXPIRY:</span>
        {sliced.expiries.map((d, i) => (
          <button key={d} onClick={() => setExpiryIdx(i)} style={{
            background: i === expiryIdx ? ORG : '#1a1a1a',
            color: i === expiryIdx ? '#000' : '#888',
            border: '1px solid ' + (i === expiryIdx ? ORG : '#333'),
            fontFamily: FONT, fontSize: 10, padding: '2px 8px',
            borderRadius: 2,
          }}>{d}</button>
        ))}
      </div>

      {/* Summary line */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 8, fontSize: 11, flexWrap: 'wrap' }}>
        <Pair label="SPOT"    value={sDol(sliced.spot)}   color={ORG} />
        <Pair label="P/C"     value={sNum(sliced.pcr)} />
        <Pair label="DTE"     value={`${sliced.dte}d`} />
        <Pair label="ATM IV"  value={sPct(sliced.atmIv)}  color={ORG} />
        <Pair label="SKEW↓"   value={sPct(sliced.skewDn)} />
        <Pair label="SKEW↑"   value={sPct(sliced.skewUp)} />
        <Pair label="CALL OI" value={sCnt(sliced.callOI)} color={GRN} />
        <Pair label="PUT OI"  value={sCnt(sliced.putOI)}  color={RED} />
        {analytics && <>
          <Pair label="GEX"   value={fmtBn(analytics.profile.totalGEX)} color={Number.isFinite(analytics.profile.totalGEX) ? (analytics.profile.totalGEX >= 0 ? GRN : RED) : MUT} />
          <Pair label="FLIP"  value={analytics.profile.flipStrike != null ? sDol(analytics.profile.flipStrike) : '—'} color={ORG} />
          <Pair label="MAX PAIN" value={Number.isFinite(analytics.maxPainStrike) ? sDol(analytics.maxPainStrike) : '—'} color={ORG} />
          <Pair label="25Δ SKEW" value={sPct(analytics.skewN)} />
        </>}
      </div>

      {/* GEX profile + IV skew side by side */}
      {analytics && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 8, height: 200 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: ORG, fontSize: 10, letterSpacing: 1, marginBottom: 2 }}>
              GAMMA EXPOSURE BY STRIKE
            </div>
            <Plot
              data={[{
                type: 'bar', orientation: 'h',
                y: analytics.profile.rows.map(r => r.strike),
                x: analytics.profile.rows.map(r => r.gex),
                marker: { color: analytics.profile.rows.map(r => r.gex >= 0 ? GAIN : LOSS) },
                hovertemplate: 'Strike $%{y}<br>GEX %{x:.2s}<extra></extra>',
              } as unknown as Plotly.Data]}
              layout={{
                paper_bgcolor: '#090909', plot_bgcolor: '#090909',
                margin: { l: 50, r: 10, t: 4, b: 24 },
                font: { color: '#e0e0e0', family: 'Consolas, monospace', size: 9 },
                xaxis: { gridcolor: '#1a1a1a', zerolinecolor: ACC, zerolinewidth: 2, color: MUT },
                yaxis: { gridcolor: '#1a1a1a', color: '#e0e0e0', autorange: 'reversed' as unknown as boolean },
                shapes: [
                  // Spot reference line
                  { type: 'line', xref: 'paper', x0: 0, x1: 1, yref: 'y',
                    y0: sliced.spot, y1: sliced.spot,
                    line: { color: ACC, width: 1, dash: 'dot' } },
                  // Flip point
                  ...(analytics.profile.flipStrike != null ? [{
                    type: 'line', xref: 'paper', x0: 0, x1: 1, yref: 'y',
                    y0: analytics.profile.flipStrike, y1: analytics.profile.flipStrike,
                    line: { color: '#ffee33', width: 1, dash: 'longdash' },
                  }] : []),
                  // Max pain pin
                  { type: 'line', xref: 'paper', x0: 0, x1: 1, yref: 'y',
                    y0: analytics.maxPainStrike, y1: analytics.maxPainStrike,
                    line: { color: '#cc66ff', width: 1, dash: 'longdash' } },
                ],
                showlegend: false,
              } as unknown as Partial<Plotly.Layout>}
              config={{ displayModeBar: false, responsive: true }}
              style={{ width: '100%', height: '100%' }}
              useResizeHandler
            />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: ORG, fontSize: 10, letterSpacing: 1, marginBottom: 2 }}>
              IV SKEW &nbsp;·&nbsp; TERM STRUCTURE
            </div>
            <Plot
              data={[
                {
                  type: 'scatter', mode: 'lines+markers', name: 'IV skew',
                  x: analytics.skew.map(p => p.strike),
                  y: analytics.skew.map(p => p.iv * 100),
                  line: { color: ACC, width: 1.5 },
                  marker: { size: 5, color: analytics.skew.map(p => p.type === 'put' ? LOSS : GAIN) },
                  hovertemplate: 'Strike $%{x}<br>IV %{y:.1f}%<extra></extra>',
                  yaxis: 'y',
                  xaxis: 'x',
                },
                {
                  type: 'scatter', mode: 'lines+markers', name: 'Term',
                  x: analytics.term.map(p => p.dte),
                  y: analytics.term.map(p => p.atmIv * 100),
                  line: { color: '#ffee33', width: 1.5, dash: 'dot' },
                  marker: { size: 5, color: '#ffee33' },
                  hovertemplate: 'DTE %{x}d<br>ATM IV %{y:.1f}%<extra></extra>',
                  yaxis: 'y2',
                  xaxis: 'x2',
                },
              ] as unknown as Plotly.Data[]}
              layout={{
                paper_bgcolor: '#090909', plot_bgcolor: '#090909',
                margin: { l: 40, r: 40, t: 4, b: 24 },
                font: { color: '#e0e0e0', family: 'Consolas, monospace', size: 9 },
                grid: { rows: 1, columns: 2, pattern: 'independent' },
                xaxis:  { gridcolor: '#1a1a1a', color: MUT, title: { text: 'STRIKE', font: { size: 8 } } },
                yaxis:  { gridcolor: '#1a1a1a', color: MUT, ticksuffix: '%' },
                xaxis2: { gridcolor: '#1a1a1a', color: MUT, title: { text: 'DTE', font: { size: 8 } } },
                yaxis2: { gridcolor: '#1a1a1a', color: MUT, ticksuffix: '%' },
                showlegend: false,
              } as unknown as Partial<Plotly.Layout>}
              config={{ displayModeBar: false, responsive: true }}
              style={{ width: '100%', height: '100%' }}
              useResizeHandler
            />
          </div>
        </div>
      )}

      {/* Calls + Puts side by side */}
      <div style={{ display: 'flex', gap: 12 }}>
        <ChainTable side="calls" rows={sliced.calls} spot={sliced.spot} />
        <ChainTable side="puts"  rows={sliced.puts}  spot={sliced.spot} />
      </div>
    </div>
  );
}

// Null-safe number formatting. Option snapshots routinely carry null last/bid/
// ask/greeks for illiquid strikes; a bare `.toFixed()` on one of those threw
// "Cannot read properties of null (reading 'toFixed')" and took down the panel.
const sN   = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v)) ? v : null;
const sNum = (v: unknown, dp = 2): string => { const n = sN(v); return n === null ? '—' : n.toFixed(dp); };
const sDol = (v: unknown, dp = 2): string => { const n = sN(v); return n === null ? '—' : `$${n.toFixed(dp)}`; };
const sPct = (v: unknown, dp = 1): string => { const n = sN(v); return n === null ? '—' : `${(n * 100).toFixed(dp)}%`; };
const sCnt = (v: unknown): string => { const n = sN(v); return n === null ? '—' : n.toLocaleString(); };

// Format a large number as $X.XB / $X.XM / $X.Xk.
function fmtBn(n: number | null | undefined): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  const a = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (a >= 1e9) return `${sign}$${(a / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(2)}k`;
  return `${sign}$${a.toFixed(0)}`;
}

function nearest(rows: OptionContract[], target: number): OptionContract | undefined {
  if (rows.length === 0) return undefined;
  return rows.reduce((best, r) => Math.abs(r.strike - target) < Math.abs(best.strike - target) ? r : best);
}

function ChainTable({ side, rows, spot }: {
  // spot may be UNKNOWN (failed quote). ITM shading is a claim about moneyness;
  // with no spot we shade nothing rather than shade against a fabricated price.
  side: 'calls' | 'puts'; rows: OptionContract[]; spot: number | null;
}) {
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ color: side === 'calls' ? GRN : RED, fontSize: 10, letterSpacing: 1, marginBottom: 4 }}>
        {side.toUpperCase()}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
        <thead>
          <tr style={{ color: ORG, fontSize: 9, letterSpacing: 1 }}>
            <th style={th}>Strike</th>
            <th style={th}>Last</th>
            <th style={th}>Bid</th>
            <th style={th}>Ask</th>
            <th style={th}>IV</th>
            <th style={th}>OI</th>
            <th style={th}>Vol</th>
            <th style={th}>ITM</th>
            <th style={th}>Δ</th>
            <th style={th}>Γ</th>
            <th style={th}>Θ</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const itm = spot === null ? false : (side === 'calls' ? r.strike < spot : r.strike > spot);
            return (
              <tr key={r.strike} style={{ background: itm ? '#0a1a0a' : 'transparent' }}>
                <td style={td}>{sDol(r.strike)}</td>
                <td style={td}>{sDol(r.last)}</td>
                <td style={td}>{sDol(r.bid)}</td>
                <td style={td}>{sDol(r.ask)}</td>
                <td style={td}>{sPct(r.iv)}</td>
                <td style={td}>{sCnt(r.openInterest)}</td>
                <td style={td}>{sCnt(r.volume)}</td>
                <td style={td}>{itm ? '✓' : ''}</td>
                <td style={td}>{sNum(r.delta, 3)}</td>
                <td style={td}>{sNum(r.gamma, 4)}</td>
                <td style={td}>{sNum(r.theta, 3)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const th: React.CSSProperties = { textAlign: 'right', padding: '2px 4px', borderBottom: '1px solid #222' };
const td: React.CSSProperties = { textAlign: 'right', padding: '2px 4px', borderBottom: '1px solid #161616', color: '#e0e0e0' };

function Pair({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <span>
      <span style={{ color: MUT, fontSize: 10, marginRight: 4 }}>{label}</span>
      <span style={{ color: color ?? '#e0e0e0', fontWeight: 'bold' }}>{value}</span>
    </span>
  );
}

function Cent({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100%', color: '#333', fontSize: 11, letterSpacing: 2, fontFamily: FONT,
    }}>{children}</div>
  );
}
